import React, { useState, useEffect, useRef } from 'react';
import { Download, Key, Compass, Scroll, Shield, Crown, Sparkles, MapPin, Gem, Map, Star, Zap, Lock, CheckCircle, Copy, Upload, Dices, KeyRound, BookUser, Sprout, VenetianMask, BookKey, Swords, Database, Handshake, Eye, LockIcon, MoonStarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { BaseDialog } from '@/components/ui/base-dialog';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from '@/hooks/useToast';
import { useLoginActions } from '@/hooks/useLoginActions';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useUploadFile } from '@/hooks/useUploadFile';
import { generateSecretKey, nip19 } from 'nostr-tools';
import { sanitizeFilename } from '@/lib/security';

interface SignupDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const SignupDialog: React.FC<SignupDialogProps> = ({ isOpen, onClose }) => {
  const [step, setStep] = useState<'welcome' | 'generate' | 'download' | 'profile' | 'done'>('welcome');
  const [isLoading, setIsLoading] = useState(false);
  const [nsec, setNsec] = useState('');
  const [showSparkles, setShowSparkles] = useState(false);
  const [keySecured, setKeySecured] = useState<'none' | 'copied' | 'downloaded'>('none');
  const [profileData, setProfileData] = useState({
    name: '',
    about: '',
    picture: ''
  });
  const login = useLoginActions();
  const { mutateAsync: publishEvent, isPending: isPublishing } = useNostrPublish();
  const { mutateAsync: uploadFile, isPending: isUploading } = useUploadFile();
  const avatarFileInputRef = useRef<HTMLInputElement>(null);

  // Generate a proper nsec key using nostr-tools
  const generateKey = () => {
    setIsLoading(true);
    setShowSparkles(true);
    
    setTimeout(() => {
      try {
        const sk = generateSecretKey();
        setNsec(nip19.nsecEncode(sk));
        setStep('download');
        
        toast({
          title: 'A new key has been forged.',
          description: 'Keep it secret from all.',
        });
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to generate key. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
        setShowSparkles(false);
      }
    }, 1000);
  };

  const downloadKey = () => {
    try {
      const blob = new Blob([nsec], { type: 'text/plain; charset=utf-8' });
      const url = globalThis.URL.createObjectURL(blob);
      const filename = sanitizeFilename('surveil-guild-key.txt');
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      globalThis.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setKeySecured('downloaded');

      toast({
        title: 'Key Secured',
        description: 'Your key has been safely stored.',
      });
    } catch (error) {
      toast({
        title: 'Download failed',
        description: 'Could not download the key file. Please copy it manually.',
        variant: 'destructive',
      });
    }
  };

  const copyKey = () => {
    navigator.clipboard.writeText(nsec);
    setKeySecured('copied');
    toast({
      title: 'Copied to clipboard',
      description: 'Your key has been copied.',
    });
  };

  const finishKeySetup = () => {
    try {
      login.nsec(nsec);
      setStep('profile');
    } catch (error) {
      toast({
        title: 'Login Failed',
        description: 'Failed to login with the generated key. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    e.target.value = '';

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please select an image file for your avatar.',
        variant: 'destructive',
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Avatar image must be smaller than 5MB.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const tags = await uploadFile(file);
      const url = tags[0]?.[1];
      if (url) {
        setProfileData(prev => ({ ...prev, picture: url }));
        toast({
          title: 'Avatar uploaded!',
          description: 'Your avatar has been uploaded successfully.',
        });
      }
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: 'Failed to upload avatar. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const finishSignup = async (skipProfile = false) => {
    try {
      if (!skipProfile && (profileData.name || profileData.about || profileData.picture)) {
        const metadata: Record<string, string> = {};
        if (profileData.name) metadata.name = profileData.name;
        if (profileData.about) metadata.about = profileData.about;
        if (profileData.picture) metadata.picture = profileData.picture;

        await publishEvent({
          kind: 0,
          content: JSON.stringify(metadata),
        });

        toast({
          title: 'Profile Created',
          description: 'Your profile has been created.',
        });
      }

      setStep('done');
      setTimeout(() => {
        onClose();
        toast({
          title: 'Welcome, Initiate',
          description: 'You are now part of the Surveil network.',
        });
      }, 2000);

    } catch (error) {
      toast({
        title: 'Profile Setup Failed',
        description: 'Your account was created but profile setup failed. You can update it later.',
        variant: 'destructive',
      });
      
      setStep('done');
      setTimeout(() => {
        onClose();
        toast({
          title: 'Welcome, Initiate',
          description: 'You are now part of the Surveil network.',
        });
      }, 2000);
    }
  };

  const getTitle = () => {
    if (step === 'welcome') return <span className="flex items-center justify-center gap-2"><BookUser className="w-5 h-5 text-primary" />Join the Guild</span>;
    if (step === 'generate') return <span className="flex items-center justify-center gap-2"><KeyRound className="w-5 h-5 text-primary" />Forge Your Key</span>;
    if (step === 'download') return <span className="flex items-center justify-center gap-2"><Lock className="w-5 h-5 text-primary" />Secure Your Key</span>;
    if (step === 'profile') return <span className="flex items-center justify-center gap-2"><VenetianMask className="w-5 h-5 text-primary" />Create Your Profile</span>;
    return <span className="flex items-center justify-center gap-2"><VenetianMask className="w-5 h-5 text-primary" />Welcome, Initiate</span>;
  };

  const getDescription = () => {
    if (step === 'welcome') return <div className="text-center">The shadows beckon. Enter the network.</div>;
    if (step === 'generate') return <div className="text-center">A new key is being forged for you.</div>;
    if (step === 'download') return <div className="text-center">This key is your password. Keep it secret to all.</div>;
    if (step === 'profile') return <div className="text-center">Establish your identity within the network.</div>;
    return <div className="text-center">You are now part of the Surveil network.</div>;
  };

  useEffect(() => {
    if (isOpen) {
      setStep('welcome');
      setIsLoading(false);
      setNsec('');
      setShowSparkles(false);
      setKeySecured('none');
      setProfileData({ name: '', about: '', picture: '' });
    }
  }, [isOpen]);

  useEffect(() => {
    if (showSparkles) {
      const interval = setInterval(() => {}, 100);
      return () => clearInterval(interval);
    }
  }, [showSparkles]);

  return (
    <BaseDialog 
      isOpen={isOpen} 
      onOpenChange={onClose}
      size="auth"
      title={<span className='font-semibold text-center text-lg'>{getTitle()}</span>}
      description={<span className='text-slate-400'>{getDescription()}</span>}
      headerClassName='px-6 pt-6 pb-1 relative flex-shrink-0'
      contentClassName='flex flex-col max-h-[90vh] bg-slate-900 text-slate-100 border-slate-700'
    >
      <div className='px-6 pt-2 pb-4 space-y-4 overflow-y-auto flex-1'>
        {step === 'welcome' && (
          <div className='text-center space-y-4'>
            <div className='relative p-6 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-950'>
              <div className='flex justify-center items-center space-x-4 mb-3'>
                <div className='relative'>
                  <Dices className='w-12 h-12 text-primary animate-pulse' />
                </div>
                <VenetianMask className='w-16 h-16 text-primary/80 animate-spin-slow' />
                <div className='relative'>
                  <BookKey className='w-12 h-12 text-primary animate-pulse' style={{animationDelay: '0.5s'}} />
                </div>
              </div>
              
              <div className='grid grid-cols-1 gap-2 text-sm'>
                <div className='flex items-center justify-center gap-2 text-slate-400'>
                  <Shield className='w-4 h-4' />
                  Secure & Decentralized
                </div>
                <div className='flex items-center justify-center gap-2 text-slate-400'>
                  <Swords className='w-4 h-4' />
                  Build Your Collection
                </div>
                <div className='flex items-center justify-center gap-2 text-slate-400'>
                  <Handshake className='w-4 h-4' />
                  Share deck building secrets
                </div>
              </div>
            </div>

            <div className='space-y-3'>
              <p className='text-slate-400 px-5'>
                Your initiation begins now.<br></br>Forge your key to enter the network.
              </p>
              
              <Button
                className='w-full rounded-full py-6 text-lg font-semibold bg-primary hover:bg-primary/90 transform transition-all duration-200 hover:scale-105 shadow-lg'
                onClick={() => setStep('generate')}
              >
                <Eye className='w-5 h-5 mr-2' />
                Begin Initiation
              </Button>
            </div>
          </div>
        )}

        {step === 'generate' && (
          <div className='text-center space-y-4'>
            <div className='relative p-6 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-950 overflow-hidden'>
              {showSparkles && (
                <div className='absolute inset-0'>
                  {[...Array(12)].map((_, i) => (
                    <Sparkles 
                      key={i}
                      className={`absolute w-4 h-4 text-primary/50 animate-ping`}
                      style={{
                        left: `${Math.random() * 80 + 10}%`,
                        top: `${Math.random() * 80 + 10}%`,
                        animationDelay: `${Math.random() * 2}s`
                      }}
                    />
                  ))}
                </div>
              )}
              
              <div className='relative z-10'>
                {isLoading ? (
                  <div className='space-y-3'>
                    <div className='relative'>
                      <Key className='w-20 h-20 text-primary mx-auto animate-pulse' />
                      <div className='absolute inset-0 flex items-center justify-center'>
                        <div className='w-24 h-24 border-4 border-primary/50 border-t-transparent rounded-full animate-spin'></div>
                      </div>
                    </div>
                    <div className='space-y-2'>
                      <p className='text-lg font-semibold text-primary flex items-center justify-center gap-2'>
                        <Sparkles className='w-5 h-5' />
                        Forging your key...
                      </p>
                      <p className='text-sm text-slate-400'>
                        This may take a moment
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className='space-y-3'>
                    <Key className='w-20 h-20 text-primary mx-auto' />
                    <div className='space-y-2'>
                      <p className='text-lg font-semibold'>
                        Ready to forge your key?
                      </p>
                      <p className='text-sm text-slate-400 px-5'>
                        This key is your password.<br></br>It's completely unique and secure, and your only way to enter the guild halls.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {!isLoading && (
              <Button
                className='w-full rounded-full py-6 text-lg font-semibold bg-primary hover:bg-primary/90 transform transition-all duration-200 hover:scale-105 shadow-lg'
                onClick={generateKey}
                disabled={isLoading}
              >
                <Sparkles className='w-5 h-5 mr-2' />
                Forge My Key
              </Button>
            )}
          </div>
        )}

        {step === 'download' && (
          <div className='text-center space-y-4'>
            <div className='relative p-6 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-950 overflow-hidden'>
              <div className='absolute inset-0 pointer-events-none'>
                <Eye className='absolute top-3 left-4 w-3 h-3 text-primary/50 animate-pulse' style={{animationDelay: '0s'}} />
                <Eye className='absolute top-6 right-6 w-3 h-3 text-primary/50 animate-pulse' style={{animationDelay: '0.5s'}} />
                <Eye className='absolute bottom-4 left-6 w-3 h-3 text-primary/50 animate-pulse' style={{animationDelay: '1s'}} />
                <Eye className='absolute bottom-3 right-4 w-3 h-3 text-primary/50 animate-pulse' style={{animationDelay: '1.5s'}} />
              </div>
              
              <div className='relative z-10 flex justify-center items-center mb-3'>
                <div className='relative'>
                  <div className='w-16 h-16 bg-gradient-to-br from-primary/20 to-primary/30 rounded-full flex items-center justify-center shadow-lg animate-pulse'>
                    <Key className='w-8 h-8 text-primary' />
                  </div>
                  <div className='absolute -top-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center animate-bounce'>
                    <Sparkles className='w-3 h-3 text-white' />
                  </div>
                </div>
              </div>
              
              <div className='relative z-10 space-y-2'>
                <p className='text-base font-semibold'>
                  Your new key is ready!
                </p>
                
                <div className='relative mx-auto max-w-sm'>
                  <div className='p-3 bg-gradient-to-r from-slate-800/40 via-slate-900/20 to-slate-800/40 rounded-lg border-2 border-slate-700 shadow-md'>
                    <div className='flex items-center gap-2 mb-1'>
                      <Scroll className='w-3 h-3 text-primary' />
                      <span className='text-xs font-bold text-primary'>
                        Important
                      </span>
                    </div>
                    <p className='text-xs text-slate-400 italic'>
                      "Guard this key with your life, initiate. Once lost, it shall be gone forever."
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className='relative p-3 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border-2 border-dashed border-primary/50 shadow-inner'>
              <div className='flex items-center gap-2 mb-2'>
                <Lock className='w-4 h-4 text-primary' />
                <span className='text-sm font-medium text-primary'>
                  Your Secret Key
                </span>
              </div>
              <div className='p-2 bg-slate-950/90 rounded-lg border border-slate-700'>
                <code className='text-xs break-all font-mono text-slate-100'>{nsec}</code>
              </div>
            </div>

            <div className='space-y-3'>
              <div className='text-center'>
                <p className='text-sm font-medium text-slate-400 mb-2'>
                  Choose how to secure your key:
                </p>
              </div>

              <div className='grid grid-cols-1 gap-2'>
                <Card className={`cursor-pointer transition-all duration-200 ${
                  keySecured === 'copied' 
                    ? 'ring-2 ring-primary bg-primary/10' 
                    : 'hover:bg-slate-800/50 bg-slate-800'
                }`}>
                  <CardContent className='p-3'>
                    <Button
                      variant="ghost"
                      className='w-full h-auto p-0 justify-start'
                      onClick={copyKey}
                    >
                      <div className='flex items-center gap-3 w-full'>
                        <div className={`p-1.5 rounded-lg ${
                          keySecured === 'copied' 
                            ? 'bg-primary/20' 
                            : 'bg-slate-700'
                        }`}>
                          {keySecured === 'copied' ? (
                            <CheckCircle className='w-4 h-4 text-primary' />
                          ) : (
                            <Copy className='w-4 h-4 text-slate-400' />
                          )}
                        </div>
                        <div className='flex-1 text-left'>
                          <div className='font-medium text-sm'>
                            Copy to Clipboard
                          </div>
                          <div className='text-xs text-slate-400'>
                            Save to password manager
                          </div>
                        </div>
                        {keySecured === 'copied' && (
                          <div className='text-xs font-medium text-primary'>
                            ✓ Copied
                          </div>
                        )}
                      </div>
                    </Button>
                  </CardContent>
                </Card>

                <Card className={`cursor-pointer transition-all duration-200 ${
                  keySecured === 'downloaded' 
                    ? 'ring-2 ring-primary bg-primary/10' 
                    : 'hover:bg-slate-800/50 bg-slate-800'
                }`}>
                  <CardContent className='p-3'>
                    <Button
                      variant="ghost"
                      className='w-full h-auto p-0 justify-start'
                      onClick={downloadKey}
                    >
                      <div className='flex items-center gap-3 w-full'>
                        <div className={`p-1.5 rounded-lg ${
                          keySecured === 'downloaded' 
                            ? 'bg-primary/20' 
                            : 'bg-slate-700'
                        }`}>
                          {keySecured === 'downloaded' ? (
                            <CheckCircle className='w-4 h-4 text-primary' />
                          ) : (
                            <Download className='w-4 h-4 text-slate-400' />
                          )}
                        </div>
                        <div className='flex-1 text-left'>
                          <div className='font-medium text-sm'>
                            Download as File
                          </div>
                          <div className='text-xs text-slate-400'>
                            Save as surveil-guild-key.txt file
                          </div>
                        </div>
                        {keySecured === 'downloaded' && (
                          <div className='text-xs font-medium text-primary'>
                            ✓ Downloaded
                          </div>
                        )}
                      </div>
                    </Button>
                  </CardContent>
                </Card>
              </div>

              <Button
                className={`w-full rounded-full py-4 text-base font-semibold transform transition-all duration-200 shadow-lg ${
                  keySecured !== 'none'
                    ? 'bg-primary hover:bg-primary/90 hover:scale-105'
                    : 'bg-slate-800 text-slate-400 cursor-not-allowed'
                }`}
                onClick={finishKeySetup}
                disabled={keySecured === 'none'}
              >
                <LockIcon className='w-4 h-4 mr-2 flex-shrink-0' />
                <span className="text-center leading-tight">
                  {keySecured === 'none' ? (
                    <>
                      Please secure your key first
                    </>
                  ) : (
                    <>
                      <span className="hidden sm:inline">My Key is Safe - Continue</span>
                      <span className="sm:hidden">Key Secured - Continue</span>
                    </>
                  )}
                </span>
              </Button>
            </div>
          </div>
        )}

        {step === 'profile' && (
          <div className='text-center space-y-4'>
            <div className='relative p-6 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-950 overflow-hidden'>
              <div className='absolute inset-0 pointer-events-none'>
                <Sparkles className='absolute top-3 left-4 w-3 h-3 text-primary/50 animate-pulse' style={{animationDelay: '0s'}} />
                <Eye className='absolute top-6 right-6 w-3 h-3 text-primary/50 animate-pulse' style={{animationDelay: '0.5s'}} />
                <VenetianMask className='absolute bottom-4 left-6 w-3 h-3 text-primary/50 animate-pulse' style={{animationDelay: '1s'}} />
              </div>
              
              <div className='relative z-10 flex justify-center items-center mb-3'>
                <div className='relative'>
                  <div className='w-16 h-16 bg-gradient-to-br from-primary/20 to-primary/30 rounded-full flex items-center justify-center shadow-lg'>
                    <VenetianMask className='w-8 h-8 text-primary' />
                  </div>
                  <div className='absolute -top-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center animate-bounce'>
                    <MoonStarIcon className='w-3 h-3 text-white' />
                  </div>
                </div>
              </div>
              
              <div className='relative z-10 space-y-2'>
                <p className='text-base font-semibold'>
                  Welcome, initiate.<br></br>Now, what persona shall you adopt?
                </p>
                
                <p className='text-sm text-slate-400'>
                  This is how others will see you
                </p>
              </div>
            </div>

            {isPublishing && (
              <div className='relative p-4 rounded-xl bg-gradient-to-r from-slate-800/40 to-slate-900/20 border border-slate-700'>
                <div className='flex items-center justify-center gap-3'>
                  <div className='w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin' />
                  <span className='text-sm font-medium text-primary'>
                    Publishing your profile...
                  </span>
                </div>
              </div>
            )}

            <div className={`space-y-4 text-left ${isPublishing ? 'opacity-50 pointer-events-none' : ''}`}>
              <div className='space-y-2'>
                <label htmlFor='profile-name' className='text-sm font-medium'>
                  Display Name
                </label>
                <Input
                  id='profile-name'
                  value={profileData.name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder='Your name'
                  className='rounded-lg bg-slate-800 border-slate-700 text-slate-100'
                  disabled={isPublishing}
                />
              </div>

              <div className='space-y-2'>
                <label htmlFor='profile-about' className='text-sm font-medium'>
                  Bio
                </label>
                <Textarea
                  id='profile-about'
                  value={profileData.about}
                  onChange={(e) => setProfileData(prev => ({ ...prev, about: e.target.value }))}
                  placeholder='Tell others about yourself...'
                  className='rounded-lg resize-none bg-slate-800 border-slate-700 text-slate-100'
                  rows={3}
                  disabled={isPublishing}
                />
              </div>

              <div className='space-y-2'>
                <label htmlFor='profile-picture' className='text-sm font-medium'>
                  Avatar
                </label>
                <div className='flex gap-2'>
                  <Input
                    id='profile-picture'
                    value={profileData.picture}
                    onChange={(e) => setProfileData(prev => ({ ...prev, picture: e.target.value }))}
                    placeholder='https://example.com/your-avatar.jpg'
                    className='rounded-lg flex-1 bg-slate-800 border-slate-700 text-slate-100'
                    disabled={isPublishing}
                  />
                  <input
                    type='file'
                    accept='image/*'
                    className='hidden'
                    ref={avatarFileInputRef}
                    onChange={handleAvatarUpload}
                  />
                  <Button
                    type='button'
                    variant='outline'
                    size='icon'
                    onClick={() => avatarFileInputRef.current?.click()}
                    disabled={isUploading || isPublishing}
                    className='rounded-lg shrink-0'
                    title='Upload avatar image'
                  >
                    {isUploading ? (
                      <div className='w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin' />
                    ) : (
                      <Upload className='w-4 h-4' />
                    )}
                  </Button>
                </div>
              </div>
            </div>

            <div className='space-y-3'>
              <Button
                className='w-full rounded-full py-4 text-base font-semibold bg-primary hover:bg-primary/90 transform transition-all duration-200 hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none'
                onClick={() => finishSignup(false)}
                disabled={isPublishing || isUploading}
              >
                {isPublishing ? (
                  <>
                    <div className='w-4 h-4 mr-2 border-2 border-current border-t-transparent rounded-full animate-spin' />
                    Creating Profile...
                  </>
                ) : (
                  <>
                    <VenetianMask className='w-4 h-4 mr-2' />
                    Create Profile & Finish
                  </>
                )}
              </Button>
              
              <Button
                variant='outline'
                className='w-full rounded-full py-3 disabled:opacity-50 disabled:cursor-not-allowed'
                onClick={() => finishSignup(true)}
                disabled={isPublishing || isUploading}
              >
                {isPublishing ? (
                  <>
                    <div className='w-4 h-4 mr-2 border-2 border-current border-t-transparent rounded-full animate-spin' />
                    Setting up account...
                  </>
                ) : (
                  'Skip for now'
                )}
              </Button>
            </div>
          </div>
        )}
        
        {step === 'done' && (
            <div className='flex justify-center items-center py-8'>
              <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary'></div>
            </div>
        )}
      </div>
    </BaseDialog>
  );
};

export default SignupDialog;
